import React from "react";
import { ArrowRight } from "lucide-react";
import { LeadForm } from "@/pages/RequestReview";

const HOME_URL = "https://marisdesk.com/home";
const LOGIN_URL = "https://marisdesk.com/desk/login";
const SUPPORT_EMAIL = "support@marisdesk.com";

const NotMark = () => (
  <span
    aria-hidden
    className="inline-flex items-center justify-center shrink-0"
    style={{
      height: 22,
      width: 22,
      borderRadius: 999,
      border: "1px solid #B94C4C",
      color: "#B94C4C",
    }}
  >
    <svg viewBox="0 0 14 14" width="10" height="10" fill="none">
      <path
        d="M3.5 3.5l7 7M10.5 3.5l-7 7"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

const Eyebrow = ({ children, testid }) => (
  <span className="md-eyebrow" data-testid={testid}>
    {children}
  </span>
);

const Header = ({ onCtaClick }) => (
  <header
    className="w-full"
    style={{ background: "var(--md-bg)", borderBottom: "1px solid var(--md-line)" }}
    data-testid="audit-header"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-4">
      <a
        href={HOME_URL}
        className="font-display text-[1.4rem] sm:text-[1.55rem] tracking-tight"
        style={{ color: "var(--md-ink)" }}
        data-testid="audit-brand"
      >
        Maris Desk
      </a>
      <div className="flex items-center gap-2 sm:gap-5">
        <button
          type="button"
          onClick={onCtaClick}
          className="md-btn-primary"
          data-testid="audit-nav-cta"
        >
          Claim Your Audit
        </button>
        <a
          href={LOGIN_URL}
          className="md-btn-ghost hidden sm:inline-flex"
          data-testid="audit-nav-login"
        >
          Existing Client Login
        </a>
      </div>
    </div>
  </header>
);

const Hero = ({ onCtaClick }) => (
  <section
    className="relative"
    style={{ background: "var(--md-bg)" }}
    data-testid="audit-hero"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 sm:pt-14 lg:pt-16 pb-10 sm:pb-12">
      <Eyebrow testid="audit-hero-eyebrow">
        For independent hospitality teams
      </Eyebrow>
      <h1
        className="font-display mt-4 text-[2.2rem] sm:text-[2.7rem] lg:text-[3.2rem] leading-[1.06] max-w-3xl"
        style={{ color: "var(--md-ink)" }}
        data-testid="audit-hero-headline"
      >
        Stop managing guest requests on WhatsApp and sticky notes.
      </h1>
      <p
        className="mt-5 max-w-2xl text-[1rem] sm:text-[1.08rem] leading-relaxed"
        style={{ color: "var(--md-ink-soft)" }}
        data-testid="audit-hero-subhead"
      >
        Maris Desk is the dedicated backend operational backbone for small
        hotels, villas, and boutique stays. Eliminate dropped requests, hold
        shifts accountable, and protect your 5-star reviews — without clunky
        apps or complex software.
      </p>
      <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="button"
          onClick={onCtaClick}
          className="md-btn-primary"
          data-testid="audit-hero-cta"
        >
          Claim Your 10-Minute Guest Flow Audit
          <ArrowRight size={18} />
        </button>
        <p className="text-sm" style={{ color: "var(--md-ink-mute)" }}>
          Free · 10 minutes · No setup required
        </p>
      </div>
    </div>
  </section>
);

const clarifierColumns = [
  {
    label: "NOT A PMS",
    body:
      "We don't manage room billing or check-ins. We power human execution after the guest arrives.",
  },
  {
    label: "NOT A GUEST APP",
    body:
      "Guests hate downloading apps for a weekend stay. This works entirely on the backend for your staff.",
  },
  {
    label: "NOT A GENERIC CHAT",
    body:
      "Unlike unmonitored messaging threads, every request has a countdown timer, room number context, and escalation safety nets.",
  },
];

const SectionClarifier = () => (
  <section
    className="relative py-14 sm:py-16"
    style={{ background: "var(--md-bg-sand)" }}
    data-testid="audit-clarifier"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <div className="max-w-2xl">
        <Eyebrow>The anti-category</Eyebrow>
        <h2
          className="font-display mt-4 text-[1.85rem] sm:text-[2.2rem] leading-[1.12]"
          style={{ color: "var(--md-ink)" }}
        >
          Built for independent hospitality teams, not large chains.
        </h2>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
        {clarifierColumns.map((col, i) => (
          <div
            key={col.label}
            data-testid={`audit-clarifier-${i}`}
            className="flex flex-col gap-3 pt-5"
            style={{ borderTop: "1px solid var(--md-line)" }}
          >
            <div className="flex items-center gap-2.5">
              <NotMark />
              <span
                className="text-[0.72rem] tracking-[0.18em] uppercase font-semibold"
                style={{ color: "#B94C4C" }}
              >
                {col.label}
              </span>
            </div>
            <p
              className="text-[0.98rem] leading-relaxed"
              style={{ color: "var(--md-ink-soft)" }}
            >
              {col.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const painItems = [
  {
    title: "Open Loops",
    body:
      "A towel request gets answered, but no one verifies it was actually delivered to Room 104.",
  },
  {
    title: "No Owner",
    body:
      "An asset issue is shouted across two-way radios, but no individual staff member takes actual accountability.",
  },
  {
    title: "Shift Blindspots",
    body:
      "Verbal agreements made during the morning shift are completely invisible to the evening team.",
  },
  {
    title: "Delayed Follow-Ups",
    body:
      "Maintenance fixes a problem, but no one circles back to ensure the guest is happy before checkout.",
  },
  {
    title: "Discovered Late",
    body: "Management only finds out about a guest's frustration days later via a damaging 3-star public review.",
  },
];

const SectionPain = () => (
  <section
    className="relative py-14 sm:py-16"
    style={{ background: "var(--md-bg)" }}
    data-testid="audit-pain"
  >
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <div className="max-w-2xl">
        <Eyebrow>The vulnerabilities</Eyebrow>
        <h2
          className="font-display mt-4 text-[1.85rem] sm:text-[2.2rem] leading-[1.12]"
          style={{ color: "var(--md-ink)" }}
        >
          The 5 silent leaks in small-scale hospitality.
        </h2>
      </div>
      <ol className="mt-9 sm:mt-10" data-testid="audit-pain-list">
        {painItems.map((item, i) => (
          <li
            key={item.title}
            data-testid={`audit-pain-${i}`}
            className="grid grid-cols-[56px_1fr] sm:grid-cols-[80px_180px_1fr] gap-x-4 sm:gap-x-6 gap-y-1 py-5"
            style={{ borderTop: "1px solid var(--md-line)" }}
          >
            <span
              className="font-display text-[1.55rem] sm:text-[1.7rem]"
              style={{ color: "var(--md-ink-mute)" }}
            >
              {String(i + 1).padStart(2, "0")}
            </span>
            <span
              className="text-[0.72rem] tracking-[0.18em] uppercase font-semibold self-baseline col-start-2 sm:col-start-2"
              style={{ color: "var(--md-blue)" }}
            >
              {item.title}
            </span>
            <span
              className="text-[0.98rem] leading-relaxed col-start-2 sm:col-start-3 sm:row-start-1"
              style={{ color: "var(--md-ink-soft)" }}
            >
              {item.body}
            </span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const SectionSolution = ({ onCtaClick }) => (
  <section
    className="relative py-14 sm:py-16"
    style={{ background: "var(--md-bg-sand)" }}
    data-testid="audit-solution"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
      <Eyebrow>The bridge</Eyebrow>
      <h2
        className="font-display mt-4 text-[1.9rem] sm:text-[2.3rem] leading-[1.1]"
        style={{ color: "var(--md-ink)" }}
        data-testid="audit-solution-headline"
      >
        Bring a predictable rhythm to your front desk.
      </h2>
      <p
        className="mt-5 max-w-2xl mx-auto text-[1rem] sm:text-[1.05rem] leading-relaxed"
        style={{ color: "var(--md-ink-soft)" }}
        data-testid="audit-solution-body"
      >
        We are launching Maris Desk this month to help independent operators
        eliminate daily friction. Request an early flow review to see exactly
        how your current team can close open operational loops in under 15
        minutes.
      </p>
      <div
        aria-hidden
        className="mx-auto my-7 h-px w-12"
        style={{ background: "var(--md-blue)", opacity: 0.6 }}
      />
      <button
        type="button"
        onClick={onCtaClick}
        className="md-btn-primary"
        data-testid="audit-solution-cta"
      >
        Request Free Flow Review Access
        <ArrowRight size={18} />
      </button>
      <p
        className="mt-5 text-sm"
        style={{ color: "var(--md-ink-mute)" }}
      >
        Free review · No setup required · Built for small hospitality teams
      </p>
    </div>
  </section>
);

const SectionForm = React.forwardRef((_props, ref) => (
  <section
    ref={ref}
    id="form"
    className="relative py-12 sm:py-14"
    style={{ background: "var(--md-bg)" }}
    data-testid="audit-form-section"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-10">
      <div className="mb-6">
        <Eyebrow testid="audit-form-eyebrow">Request your audit</Eyebrow>
      </div>
      <LeadForm />
    </div>
  </section>
));
SectionForm.displayName = "AuditSectionForm";

const Footer = () => (
  <footer
    className="relative py-10 sm:py-12"
    style={{ background: "var(--md-bg-sand)", borderTop: "1px solid var(--md-line)" }}
    data-testid="audit-footer"
  >
    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
      <p
        className="font-display text-[1.3rem] sm:text-[1.45rem] leading-snug"
        style={{ color: "var(--md-ink)" }}
      >
        Powered by Maris Desk
      </p>
      <p
        className="mt-3 max-w-xl mx-auto text-[0.95rem] leading-relaxed"
        style={{ color: "var(--md-ink-soft)" }}
      >
        A calmer workspace for guest requests, follow-ups, daily service work,
        and manager visibility.
      </p>
      <a
        href={HOME_URL}
        className="md-btn-ghost mt-5"
        data-testid="audit-home-link"
      >
        Learn more about Maris Desk
        <ArrowRight size={16} />
      </a>
      <div className="md-divider mt-10 mx-auto max-w-md" />
      <div
        className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-x-5 gap-y-2 text-xs flex-wrap"
        style={{ color: "var(--md-ink-mute)" }}
      >
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="hover:underline"
          style={{ color: "var(--md-blue)" }}
        >
          {SUPPORT_EMAIL}
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <a href="/privacy" className="hover:underline" style={{ color: "var(--md-ink-soft)" }}>
          Privacy
        </a>
        <span aria-hidden style={{ opacity: 0.5 }}>·</span>
        <a href="/terms" className="hover:underline" style={{ color: "var(--md-ink-soft)" }}>
          Terms
        </a>
      </div>
      <p className="mt-4 text-xs" style={{ color: "var(--md-ink-mute)" }}>
        © {new Date().getFullYear()} Maris Desk.
      </p>
    </div>
  </footer>
);

export default function Audit() {
  const formRef = React.useRef(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div data-testid="audit-page" style={{ background: "var(--md-bg)" }}>
      <Header onCtaClick={scrollToForm} />
      <Hero onCtaClick={scrollToForm} />
      <SectionClarifier />
      <SectionPain />
      <SectionSolution onCtaClick={scrollToForm} />
      <SectionForm ref={formRef} />
      <Footer />
    </div>
  );
}
